import api from '../../../shared/services/api';
import {
  ApplyCouponRequest,
  ApplyCouponResponse,
  ChangePlanRequest,
  ChangePlanResponse,
  SubscriptionHistoryItem,
  SubscriptionPlan,
  UserSubscription,
} from '../types/subscription.types';

export class SubscriptionService {
  private static readonly BASE_URL = '/subscriptions';

  /**
   * Get all available subscription plans
   */
  static async getPlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get(`${this.BASE_URL}/plans`);
      return response.data?.plans || [];
    } catch (error: any) {
      throw new Error(error.message || 'Error loading subscription plans');
    }
  }

  /**
   * Get current subscription details for the user
   */
  static async getCurrentSubscription(): Promise<UserSubscription> {
    try {
      const response = await api.get(`${this.BASE_URL}/current`);
      // Extract both subscription and plan data from the response
      const { subscription, plan } = response.data;

      // Combine subscription and plan data to match the SubscriptionDetails interface
      return {
        ...subscription,
        currentPlan: subscription.planId,
        planDetails: plan,
      };
    } catch (error: any) {
      throw new Error(error.message || 'Error loading current subscription');
    }
  }

  /**
   * Apply a coupon code to get discount
   */
  static async applyCoupon(
    request: ApplyCouponRequest
  ): Promise<ApplyCouponResponse> {
    try {
      const response = await api.post(
        `${this.BASE_URL}/coupon/validate`,
        request
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          isValid: false,
          message: 'Invalid coupon code',
        };
      }
      throw new Error(
        error.error || error.message || 'Error validating coupon'
      );
    }
  }

  /**
   * Change subscription plan
   */
  static async changePlan(
    request: ChangePlanRequest
  ): Promise<ChangePlanResponse> {
    try {
      const response = await api.post(`${this.BASE_URL}/change-plan`, request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        return {
          success: false,
          message: error.response.data.message,
        };
      }
      throw new Error(error.message || 'Error changing subscription plan');
    }
  }

  /**
   * Cancel subscription auto-renewal
   */
  static async cancelAutoRenewal(): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.post(`${this.BASE_URL}/cancel-renewal`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || 'Error cancelling subscription renewal');
    }
  }

  /**
   * Enable subscription auto-renewal
   */
  static async enableAutoRenewal(): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.post(`${this.BASE_URL}/enable-renewal`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || 'Error enabling subscription renewal');
    }
  }

  /**
   * Create a checkout session for subscription payment
   */
  static async createCheckoutSession(
    planId: string,
    couponCode?: string
  ): Promise<{
    sessionId: string;
    sessionUrl: string;
  }> {
    try {
      const response = await api.post(`${this.BASE_URL}/checkout`, {
        planId,
        couponCode,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Error creating checkout session'
      );
    }
  }

  /**
   * Cancel subscription at the end of the current billing period
   */
  static async cancelAtPeriodEnd(subscriptionId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.post(
        `${this.BASE_URL}/cancel-at-period-end/${subscriptionId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          'Error cancelling subscription at period end'
      );
    }
  }

  /**
   * Downgrade subscription to free plan
   */
  static async downgradeToFree(subscriptionId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.post(
        `${this.BASE_URL}/downgrade-to-free/${subscriptionId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          'Error downgrading to free plan'
      );
    }
  }

  /**
   * Downgrade from yearly to monthly plan
   */
  static async downgradeYearlyToMonthly(subscriptionId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.post(
        `${this.BASE_URL}/downgrade-yearly-to-monthly/${subscriptionId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          'Error downgrading to monthly plan'
      );
    }
  }

  /**
   * Upgrade from monthly to yearly plan
   */
  static async upgradeMonthlyToYearly(subscriptionId: string): Promise<{
    success: boolean;
    message: string;
    sessionId?: string;
    sessionUrl?: string;
  }> {
    try {
      const response = await api.post(
        `${this.BASE_URL}/upgrade-monthly-to-yearly/${subscriptionId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          'Error upgrading to yearly plan'
      );
    }
  }

  /**
   * Gets the subscription history for the current user
   * @returns A promise that resolves to an array of subscription history items
   */
  static async getSubscriptionHistory(): Promise<SubscriptionHistoryItem[]> {
    try {
      const response = await api.get<{
        subscriptions: SubscriptionHistoryItem[];
      }>('/subscriptions/history');
      return response.data.subscriptions;
    } catch (error: any) {
      console.error('Error fetching subscription history:', error);
      return [];
    }
  }

  /**
   * Apply a time-limited access coupon to create a subscription without payment
   * @param couponCode The coupon code to apply
   * @returns A promise that resolves to the result of applying the coupon
   */
  static async applyTimeLimitedAccessCoupon(
    couponCode: string
  ): Promise<{
    success: boolean;
    message: string;
    subscription?: UserSubscription;
  }> {
    try {
      const response = await api.post(`${this.BASE_URL}/apply-time-limited-coupon`, {
        couponCode
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Error applying time-limited access coupon'
      );
    }
  }
}
